import { defineStore } from 'pinia'
import { computed, type Ref, ref } from 'vue'

import { DataServiceFactory, EntraIDAuthService, EventService } from '@/factories'
import type { IDropdown, IEvent, IEventIndex, IEventType, IStoreEntity } from '@/interfaces'
import { Event, EventAddress } from '@/models'
import { EntraIDAuthConfig } from '@/services'
import { type IFullStore, useAuthStore, useBaseStore } from '@/stores'

export interface IEventStore<T extends IStoreEntity, TI = T> extends IFullStore<T, TI> {
	eventTypes: Ref<IEventType[]>
	eventTypeDropdown: Ref<IDropdown[]>
	eventDropdown: Ref<IDropdown[]>
	eventDropdownFiltered: Ref<IDropdown[]>
	initialLoad: Ref<boolean>
	recalculateHash: () => Promise<void>
	addNew: () => T
	getCode: () => Promise<string>
	getTypes: () => Promise<IEventType[]>
	getFilteredIndex: () => Promise<IEventIndex[]>
}

const authService = DataServiceFactory.createAuthService('EntraIDAuth', EntraIDAuthConfig) as EntraIDAuthService
const eventService = DataServiceFactory.createService('Event', authService) as EventService

export const useEventStore = defineStore<'event-store', IEventStore<IEvent, IEventIndex>>('event-store', () => {
	const {
		id,
		records,
		activeRecord,
		searchTerm,
		preFilters,
		currentPage,
		sortCriteria,
		storeActive,
		isDuplicating,
		activeRecordSavedHash,
		indexView,
		indexFilter,
		indexParams,
		viewList,
		filterList,
		pageSize,

		elevatedPermissions,
		currentFormHash,
		currentFormDirty,

		// Column filter methods
		columnFilters,
		activeColumnFilters,
		hasActiveColumnFilters,
		activeColumnFilterCount,
		setColumnFilter,
		clearColumnFilter,
		clearAllColumnFilters,
		getColumnFilter,
		hasColumnFilter,

		preFilteredRecords,
		searchedRecords,
		searchRecordIndex,
		searchedRecordLength,
		currentPages,
		totalPages,
		recordCount,
		previousRecord,
		nextRecord,
		orderedRecords,
		pagedRecords,

		canRead,
		canEdit,
		canAdd,
		clearActive,
		clearSearch,
		calcHash
	} = useBaseStore<IEvent, 'Event', IEventIndex>('Event')

	const eventTypes = ref<IEventType[]>([])
	const filteredIndex = ref<IEventIndex[]>([])
	const initialLoad = ref(false)

	const getAuthStore = computed(() => {
		return useAuthStore()
	})

	const eventTypeDropdown = computed(() => {
		if (!eventTypes.value.length) {
			getTypes()
		}
		return eventTypes.value.map((c: IEventType) => ({ name: c.name, code: c.id }))
	})

	const eventDropdown = computed(() => {
		if (!records.value.length) {
			getIndex()
		}
		return records.value
			.map((e: IEventIndex) => ({ name: `${e.code} - ${e.name}`, code: e.code }))
			.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
	})

	const eventDropdownFiltered = computed(() => {
		if (!filteredIndex.value.length) {
			getFilteredIndex()
		}
		return filteredIndex.value
			.map((e: IEventIndex) => ({ name: `${e.code} - ${e.name}`, code: e.code }))
			.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
	})

	async function getIndex(): Promise<IEventIndex[]> {
		// Ensure event types are loaded first
		if (!eventTypes.value.length) {
			await getTypes()
		}

		return eventService
			.getIndex()
			.then((data) => {
				// Populate typeName for each record by looking up from eventTypes
				records.value = data.map((event) => {
					const eventType = eventTypes.value.find((et) => et.id === event.type)
					// Add typeName to the event object
					event.typeName = eventType?.name || ''
					return event
				})
				return records.value
			})
			.catch((err) => {
				console.error(err)
				records.value = []
				return records.value
			})
	}

	async function getFilteredIndex(): Promise<IEventIndex[]> {
		// Ensure event types are loaded first
		if (!eventTypes.value.length) {
			await getTypes()
		}

		const date = new Date()
		date.setDate(date.getDate() - 180)

		const year = date.getFullYear()
		const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
		const day = String(date.getDate()).padStart(2, '0')

		const formattedDate = `${year}-${month}-${day}`

		return eventService
			.getIndex({ startDate: formattedDate })
			.then((data) => {
				// Populate typeName for each record by looking up from eventTypes
				filteredIndex.value = data.map((event) => {
					const eventType = eventTypes.value.find((et) => et.id === event.type)
					// Add typeName to the event object
					event.typeName = eventType?.name || ''
					return event
				})
				return filteredIndex.value
			})
			.catch((err) => {
				console.error(err)
				filteredIndex.value = []
				return filteredIndex.value
			})
	}

	async function get(id: string): Promise<Event> {
		initialLoad.value = false
		if (id === '0') {
			return addNew()
		}
		return eventService
			.get(encodeURIComponent(id))
			.then((data) => {
				activeRecord.value = data
				activeRecordSavedHash.value = calcHash(data)
				console.log('activeRecordHash', activeRecordSavedHash.value)
				return activeRecord.value
			})
			.catch((err) => {
				console.error(err)
				activeRecord.value = {} as Event
				return activeRecord.value
			})
	}

	async function recalculateHash(): Promise<void> {
		console.log(activeRecord.value)
		if (activeRecord.value) activeRecordSavedHash.value = calcHash(activeRecord.value)
	}

	function addNew(): Event {
		const record = new Event({} as Event)
		record.id = 0
		// mapOverride defaults to undefined (unknown source) until user takes action:
		// - Google Places search will set it to false
		// - Toggle override ON will set it to true
		record.addresses.add(new EventAddress({ isPrimary: true } as EventAddress))
		activeRecord.value = record
		activeRecordSavedHash.value = calcHash(record)
		return activeRecord.value
	}

	async function save(record: Event): Promise<Event> {
		console.log(record)
		if (record.id === undefined || record.id === 0) {
			return eventService
				.add(record)
				.then((data) => {
					activeRecord.value = data
					activeRecordSavedHash.value = calcHash(data)
					return activeRecord.value
				})
				.catch((err) => {
					console.error(err)
					// activeRecord.value = {} as Job
					// return activeRecord.value
					throw err
				})
		} else {
			return eventService
				.update(record)
				.then((data) => {
					activeRecord.value = data
					activeRecordSavedHash.value = calcHash(data)
					return activeRecord.value
				})
				.catch((err) => {
					console.error(err)
					throw err
				})
		}
	}

	async function getCode(): Promise<string> {
		if (!(activeRecord.value?.name && activeRecord.value?.endDate)) {
			console.log('No name or date')
			console.log(activeRecord)
			return new Promise((resolve) => {
				resolve('')
			})
		}
		return eventService
			.getCode(activeRecord.value.name, activeRecord.value.endDate)
			.then((data) => {
				return data
			})
			.catch((err) => {
				console.error(err)
				throw err
			})
	}

	async function getTypes(): Promise<IEventType[]> {
		return eventService
			.getTypes()
			.then((data) => {
				eventTypes.value = data
				return eventTypes.value
			})
			.catch((err) => {
				console.error(err)
				throw err
			})
	}

	if (!eventTypes.value.length) {
		getTypes()
	}

	if (!filteredIndex.value.length) {
		getFilteredIndex()
	}

	// if (!records.value.length) {
	// 	getIndex()
	// }

	return {
		id,
		records,
		activeRecord,
		searchTerm,
		preFilters,
		currentPage,
		sortCriteria,
		storeActive,
		isDuplicating,
		activeRecordSavedHash,
		indexView,
		indexFilter,
		indexParams,
		eventTypes,
		eventDropdown,
		eventDropdownFiltered,
		location,
		viewList,
		filterList,
		pageSize,
		initialLoad,

		elevatedPermissions,
		currentFormHash,
		currentFormDirty,
		preFilteredRecords,
		searchedRecords,
		searchRecordIndex,
		searchedRecordLength,
		currentPages,
		totalPages,
		recordCount,
		previousRecord,
		nextRecord,
		orderedRecords,
		pagedRecords,
		getAuthStore,
		eventTypeDropdown,

		clearSearch,
		clearActive,
		get,
		getIndex,
		save,
		addNew,
		getTypes,
		getCode,
		canRead,
		canEdit,
		canAdd,
		calcHash,
		recalculateHash,
		getFilteredIndex,

		// Column filter methods
		columnFilters,
		activeColumnFilters,
		hasActiveColumnFilters,
		activeColumnFilterCount,
		setColumnFilter,
		clearColumnFilter,
		clearAllColumnFilters,
		getColumnFilter,
		hasColumnFilter
	}
})
